import { fromMarkdown } from './richtext/fromMarkdown';
import type { RichTextDoc } from './richtext/schema';

/**
 * Starting points for a conversation canvas (D-1188).
 *
 * A canvas (D-1157) is one rich-text document per conversation, and until now the only one on
 * offer was an empty one. An empty document is the hardest thing to write: everybody opens it,
 * looks at it, and closes it again. These are the shapes people were going to make anyway —
 * meeting notes, a brief, a decision record — already laid out, so the work is filling them in.
 *
 * ── WHY MARKDOWN AND NOT A STORED DOCUMENT ──────────────────────────────────
 *
 * They are static content, the same for every workspace, so they are code rather than rows: no
 * migration, no seed, no API, nothing to keep in step across instances, and `fromMarkdown` (D-542)
 * is the same converter the composer uses. A template is therefore proved by a unit test rather
 * than by a deploy.
 *
 * ── WHY NO `#` HEADINGS ─────────────────────────────────────────────────────
 *
 * The body schema (D-045) has no heading node, by design — a message is not a document outline.
 * `fromMarkdown` leaves a `#` as literal text, so a section title here is a **bold paragraph**,
 * which is what the composer produces when somebody writes one by hand.
 */

export interface CanvasTemplate {
  id: string;
  /** Shown in the picker. */
  name: string;
  /** One line under the name: when to reach for this one. */
  description: string;
  /** The body, as the markdown the composer itself understands. */
  markdown: string;
}

export const CANVAS_TEMPLATES: readonly CanvasTemplate[] = [
  {
    id: 'meeting-notes',
    name: 'Meeting notes',
    description: 'Agenda, decisions and who is doing what by when.',
    markdown: [
      '**Meeting notes**',
      '',
      'Date: ',
      'Present: ',
      '',
      '**Agenda**',
      '',
      '1. ',
      '2. ',
      '3. ',
      '',
      '**Decisions**',
      '',
      '- ',
      '',
      '**Actions**',
      '',
      '- Who · what · by when',
      '',
      '**Parked**',
      '',
      '- Things raised that are not for today',
    ].join('\n'),
  },
  {
    id: 'project-brief',
    name: 'Project brief',
    description: 'What we are doing, why, and how we will know it worked.',
    markdown: [
      '**Project brief**',
      '',
      '**The problem**',
      '',
      'What is wrong today, for whom, and how often.',
      '',
      '**What we are going to do**',
      '',
      '',
      '**What we are deliberately not doing**',
      '',
      '- ',
      '',
      '**How we will know it worked**',
      '',
      '- ',
      '',
      '**Owner and dates**',
      '',
      'Owner: ',
      'Start: ',
      'Done by: ',
    ].join('\n'),
  },
  {
    id: 'decision-log',
    name: 'Decision log',
    description: 'One entry per decision, with the reasoning that produced it.',
    markdown: [
      '**Decision log**',
      '',
      'Newest first. A decision that is not written down is a decision that will be made again.',
      '',
      '**<date> — <the decision in one line>**',
      '',
      'Decided by: ',
      '',
      '> The reasoning. What was true that made this the right call.',
      '',
      '**What we considered instead**',
      '',
      '- ',
      '',
      '**What would change our mind**',
      '',
      '- ',
    ].join('\n'),
  },
  {
    id: 'onboarding',
    name: 'Onboarding checklist',
    description: 'What a new person needs in their first week, in order.',
    markdown: [
      '**Welcome**',
      '',
      'Your first week, in the order it is worth doing.',
      '',
      '**Day one**',
      '',
      '- Accounts and access',
      '- Meet your manager',
      '- Read the team channels',
      '',
      '**First week**',
      '',
      '- Sit in on a meeting of each kind',
      '- Pick up a first small task',
      '- Meet the people you will work with most',
      '',
      '**Who to ask**',
      '',
      '- ',
    ].join('\n'),
  },
  {
    id: 'weekly-update',
    name: 'Weekly update',
    description: 'Shipped, in flight, and what is in the way.',
    markdown: [
      '**Week of <date>**',
      '',
      '**Shipped**',
      '',
      '- ',
      '',
      '**In flight**',
      '',
      '- ',
      '',
      '**Blocked**',
      '',
      '- What is stuck, and who can unstick it',
      '',
      '**Next week**',
      '',
      '- ',
    ].join('\n'),
  },
  {
    id: 'incident-review',
    name: 'Incident review',
    description: 'What happened, what it cost, and what stops it next time.',
    markdown: [
      '**Incident review**',
      '',
      'Date: ',
      'Severity: ',
      'Noticed by: ',
      '',
      '**What happened**',
      '',
      '',
      '**Timeline**',
      '',
      '- ',
      '',
      '**Why it was possible**',
      '',
      'Not who did it. What made it possible.',
      '',
      '**What stops it next time**',
      '',
      '- ',
      '',
      '**What we are not changing, and why**',
      '',
      '- ',
    ].join('\n'),
  },
];

export function canvasTemplate(id: string): CanvasTemplate | undefined {
  return CANVAS_TEMPLATES.find((template) => template.id === id);
}

/** The template's body as a document the canvas can be saved with. */
export function canvasTemplateDoc(id: string): RichTextDoc | undefined {
  const template = canvasTemplate(id);
  return template ? fromMarkdown(template.markdown) : undefined;
}
